import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { observer } from 'mobx-react';
import {
    Block,
    FlexDiv,
    TextDefault,
    TextLead,
    TextTiny,
    ThemedButton,
} from '@awarego-ui/components';
import { Button, CircularProgress } from '@mui/material';
import {
    DateTimePicker,
    LocalizationProvider,
    PickersActionBar,
} from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import Modal from 'react-modal';
import styled from 'styled-components';
import TableServer from '../../../../components/table/TableServer';
import HeaderSection from '../../../../components/header/HeaderSection';
import { useStores } from '../../../../hooks/use-stores';
import EditTrainingRemindersModal from './EditTrainingRemindersModal';

const MAX_VISIBLE_SCENARIOS = 5;
const THUMB_WIDTH = 72;
const THUMB_HEIGHT = 40;

const ScenarioThumbStrip = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    flex-wrap: wrap;
    max-width: 560px;
`;

const ScenarioThumb = styled.div`
    width: ${Math.round(THUMB_WIDTH * 1.3)}px;
`;

const ScenarioThumbImage = styled.img`
    width: ${THUMB_WIDTH}px;
    height: ${THUMB_HEIGHT}px;
    object-fit: cover;
    border-radius: 4px;
    display: block;
`;

const ScenarioThumbFallback = styled.div`
    width: ${THUMB_WIDTH}px;
    height: ${THUMB_HEIGHT}px;
    border-radius: 4px;
    background: #eef0f3;
`;

const ScenarioThumbName = styled(TextTiny)`
    display: block;
    max-width: ${Math.round(THUMB_WIDTH * 1.3)}px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-top: 4px;
`;

const ShowMoreButton = styled.button`
    border: 0;
    background: transparent;
    padding: 0;
    margin-top: 10px;
    color: inherit;
    cursor: pointer;
    text-decoration: underline;
`;

const ScenarioModalContent = styled.div`
    padding: 32px;
`;

const ScenarioModalList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-top: 24px;
`;

const ScenarioModalRow = styled.div`
    display: flex;
    gap: 16px;
`;

const ScenarioModalThumb = styled.img`
    width: 160px;
    height: 90px;
    object-fit: cover;
    border-radius: 6px;
    flex-shrink: 0;
`;

const ScenarioModalThumbFallback = styled.div`
    width: 160px;
    height: 90px;
    border-radius: 6px;
    background: #eef0f3;
    flex-shrink: 0;
`;

function renderScenarioThumb(subject) {
    if (!subject.thumb) return <ScenarioThumbFallback />;
    return (
        <ScenarioThumbImage
            src={subject.thumb}
            alt={subject.name || subject.subject_id}
        />
    );
}

function renderScenarioModalThumb(subject) {
    if (!subject.thumb) return <ScenarioModalThumbFallback />;
    return (
        <ScenarioModalThumb
            src={subject.thumb}
            alt={subject.name || subject.subject_id}
        />
    );
}

function RescheduleDatePicker({ event, onUse }) {
    const [draft, setDraft] = useState(moment(event.send));

    useEffect(() => {
        setDraft(moment(event.send));
    }, [event.send]);

    const RescheduleActionBar = useCallback(
        (props) => {
            const { onCancel, ...rest } = props;
            return (
                <PickersActionBar
                    {...rest}
                    onCancel={() => {
                        setDraft(moment(event.send));
                        onCancel();
                    }}
                />
            );
        },
        [event.send]
    );

    return (
        <LocalizationProvider
            dateAdapter={AdapterMoment}
            localeText={{
                cancelButtonLabel: 'Cancel',
                okButtonLabel: 'Use',
            }}
        >
            <DateTimePicker
                label="Reschedule"
                ampm={false}
                closeOnSelect={false}
                value={draft}
                minDateTime={moment()}
                onChange={(date) => {
                    if (date) setDraft(moment(date));
                }}
                onAccept={(date) => {
                    if (!date) return;
                    const nextDate = moment(date);
                    setDraft(nextDate);
                    onUse(nextDate.toISOString());
                }}
                slots={{
                    actionBar: RescheduleActionBar,
                }}
                slotProps={{
                    actionBar: {
                        actions: ['cancel', 'accept'],
                    },
                    textField: {
                        size: 'small',
                        onClick: (e) => e.stopPropagation(),
                    },
                }}
            />
        </LocalizationProvider>
    );
}

function TrainingReminders({ companyId, training }) {
    const { automationStore, store } = useStores();
    const [events, setEvents] = useState([]);
    const [editOpen, setEditOpen] = useState(false);
    const [scenarioModalSubjects, setScenarioModalSubjects] = useState(null);
    const [query, setQuery] = useState({
        p: 0,
        n: 10,
        order: 'send desc',
        filter: '',
    });

    const editRemindersButton = useMemo(() => {
        if (!training?.canEditReminders) return null;
        return (
            <ThemedButton
                mode="secondary"
                $noTextTransform
                onClick={() => setEditOpen(true)}
                text="Edit"
            />
        );
    }, [training?.canEditReminders]);

    useEffect(() => {
        automationStore.loadScheduleOverview(companyId, training.id);
    }, [automationStore, companyId, training.id]);

    useEffect(() => {
        setEvents(
            (automationStore.scheduleOverview?.events || []).map((event) => ({
                ...event,
                dirty: false,
            }))
        );
    }, [automationStore.scheduleOverview]);

    const onChangePagingParams = useCallback((p, n) => {
        setQuery((current) =>
            current.p === p && current.n === n ? current : { ...current, p, n }
        );
    }, []);

    const onChangeSort = useCallback((sortBy) => {
        if (!sortBy || sortBy.length === 0) return;
        const order = `${sortBy[0].id} ${sortBy[0].desc ? 'desc' : 'asc'}`;
        setQuery((current) =>
            current.order === order ? current : { ...current, order }
        );
    }, []);

    const updateEvent = useCallback((eventId, values) => {
        setEvents((current) =>
            current.map((event) => {
                if (event.id !== eventId) return event;
                return {
                    ...event,
                    originalData: event.originalData || event,
                    dirty: true,
                    ...values,
                };
            })
        );
    }, []);

    const revertEvent = useCallback((eventId) => {
        setEvents((current) =>
            current.map((event) => {
                if (event.id !== eventId || !event.originalData) return event;
                return { ...event.originalData, dirty: false };
            })
        );
    }, []);

    const saveEvent = useCallback(
        async (event) => {
            const saved = await automationStore.updateScheduleEvent(
                companyId,
                training.id,
                event
            );
            if (saved) {
                setEvents((current) =>
                    current.map((row) =>
                        row.id === event.id
                            ? {
                                  ...row,
                                  dirty: false,
                                  originalData: undefined,
                              }
                            : row
                    )
                );
            }
        },
        [automationStore, companyId, training.id]
    );

    const filteredEvents = useMemo(() => {
        const term = query.filter.toLowerCase();
        let result = events.filter((event) => {
            if (!term) return true;
            return [
                event.type === 2 ? 'reminder' : 'sending',
                event.status === 2 ? 'sent' : 'scheduled',
                ...(event.subjects || []).map(
                    (subject) =>
                        `${subject.name || ''} ${
                            subject.description || ''
                        } ${subject.subject_id || ''}`
                ),
            ]
                .join(' ')
                .toLowerCase()
                .includes(term);
        });
        const [field, direction = 'asc'] = query.order.split(' ');
        result = result.sort((a, b) => {
            const aValue = a[field];
            const bValue = b[field];
            const compare =
                field === 'send'
                    ? new Date(aValue) - new Date(bValue)
                    : String(aValue || '').localeCompare(String(bValue || ''));
            return direction === 'desc' ? -compare : compare;
        });
        return result;
    }, [events, query.filter, query.order]);

    const pagedEvents = useMemo(
        () =>
            filteredEvents.slice(
                query.p * query.n,
                query.p * query.n + query.n
            ),
        [filteredEvents, query.n, query.p]
    );

    const columns = useMemo(
        () => [
            {
                Header: 'Type',
                accessor: 'type',
                Cell: (x) => (x.value === 2 ? 'Reminder' : 'Sending'),
            },
            {
                Header: 'Scenarios',
                accessor: 'subjects',
                Cell: (x) => {
                    const subjects = x.value || [];
                    const visibleSubjects = subjects.slice(
                        0,
                        MAX_VISIBLE_SCENARIOS
                    );
                    const hiddenCount = subjects.length - visibleSubjects.length;

                    return (
                        <ScenarioThumbStrip>
                            {visibleSubjects.map((subject) => (
                                <ScenarioThumb
                                    key={subject.subject_id || subject.name}
                                    title={subject.name || subject.subject_id}
                                >
                                    {renderScenarioThumb(subject)}
                                    <ScenarioThumbName>
                                        {subject.name || subject.subject_id}
                                    </ScenarioThumbName>
                                </ScenarioThumb>
                            ))}
                            {hiddenCount > 0 && (
                                <ShowMoreButton
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setScenarioModalSubjects(subjects);
                                    }}
                                >
                                    <TextTiny underline link>
                                        +{hiddenCount} show more
                                    </TextTiny>
                                </ShowMoreButton>
                            )}
                        </ScenarioThumbStrip>
                    );
                },
                disableSortBy: true,
            },
            {
                Header: 'Send date/time',
                accessor: 'send',
                Cell: (x) => {
                    const event = x.row.original;
                    const editable =
                        store.isAdmin &&
                        event.status !== 2 &&
                        moment(event.send).isAfter(moment());

                    if (!editable) return moment(x.value).format('ll[,] LT');

                    return (
                        <RescheduleDatePicker
                            event={event}
                            onUse={(send) =>
                                updateEvent(event.id, {
                                    send,
                                })
                            }
                            />
                    );
                },
            },
            {
                Header: 'Status',
                accessor: 'status',
                Cell: (x) => (x.value === 2 ? 'Sent' : 'Scheduled'),
            },
            {
                Header: ' ',
                id: 'actions',
                disableSortBy: true,
                Cell: (x) => {
                    const event = x.row.original;
                    if (!store.isAdmin || !event.dirty) return null;
                    const updating =
                        automationStore.updatingScheduleEvents.includes(event.id);
                    return (
                        <FlexDiv gap={8} alignCenter>
                            {updating && <CircularProgress size={16} />}
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    saveEvent(event);
                                }}
                            >
                                Save
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                color="secondary"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    revertEvent(event.id);
                                }}
                            >
                                Revert
                            </Button>
                        </FlexDiv>
                    );
                },
            },
        ],
        [
            automationStore.updatingScheduleEvents,
            revertEvent,
            saveEvent,
            setScenarioModalSubjects,
            store.isAdmin,
            updateEvent,
        ]
    );

    return (
        <Block gap={24} tablePadding noBorder resetMargin>
            <HeaderSection
                heading={
                    <TextLead bold>
                        Schedule & Reminders ({filteredEvents.length})
                    </TextLead>
                }
                searchPlaceholder="Search schedule"
                searchValue={query.filter}
                onSearch={(e) => {
                    const filter = e.target.value || '';
                    setQuery((current) =>
                        current.filter === filter && current.p === 0
                            ? current
                            : { ...current, p: 0, filter }
                    );
                }}
                onClearSearch={() =>
                    setQuery((current) =>
                        current.filter === '' && current.p === 0
                            ? current
                            : { ...current, p: 0, filter: '' }
                    )
                }
                buttons={editRemindersButton}
                excludeBlock
                marginLeft="16"
            />
            <TableServer
                columns={columns}
                data={pagedEvents}
                rowsTotal={filteredEvents.length}
                pagesTotal={Math.ceil(filteredEvents.length / query.n)}
                initialPageSize={query.n}
                defaultSortBy="send"
                defaultSortDescent={true}
                hidePagination={filteredEvents.length <= query.n}
                onChangePagingParams={onChangePagingParams}
                onChangeSort={onChangeSort}
                loadingData={automationStore.loadingScheduleOverview}
                manualInitialPageTrigger={query.filter}
            />
            <EditTrainingRemindersModal
                isOpen={editOpen}
                onClose={() => setEditOpen(false)}
                companyId={companyId}
                training={training}
            />
            <Modal
                ariaHideApp={false}
                isOpen={scenarioModalSubjects != null}
                onRequestClose={() => setScenarioModalSubjects(null)}
                className="Modal schedule"
            >
                <ScenarioModalContent>
                    <FlexDiv justifyEnd>
                        <Button
                            size="small"
                            onClick={() => setScenarioModalSubjects(null)}
                        >
                            Close
                        </Button>
                    </FlexDiv>
                    <TextLead bold>
                        Scenarios ({scenarioModalSubjects?.length || 0})
                    </TextLead>
                    <ScenarioModalList>
                        {(scenarioModalSubjects || []).map((subject) => (
                            <ScenarioModalRow
                                key={subject.subject_id || subject.name}
                            >
                                {renderScenarioModalThumb(subject)}
                                <FlexDiv column gap={8}>
                                    <TextDefault bold>
                                        {subject.name || subject.subject_id}
                                    </TextDefault>
                                    <TextDefault lighter>
                                        {subject.description || 'No description'}
                                    </TextDefault>
                                </FlexDiv>
                            </ScenarioModalRow>
                        ))}
                    </ScenarioModalList>
                </ScenarioModalContent>
            </Modal>
        </Block>
    );
}

export default observer(TrainingReminders);
